import { PlusOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Form, Popconfirm, Row, Select, Table, Tag, Space } from "antd";
import { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ContentHeader } from "../../components";
import ModalTextForm from "../../components/modal/ModalTextForm";
import { loaiChuKyThue, trangThaiThue } from '../../constants';
import { dateCommon, formatVND, validateMessages } from '../../helpers/common';
import { Links } from "../../helpers/Links";
import { DanhSachThueThangResponse, KyThueResponse } from '../../models/thue/Thue';
import { khoiTaoThueTongHop, layDanhSachKyThueKhongThuongXuyen, layDsThueThangTheoNam, timKiemDsThueTongHop, xoaThueTongHop } from '../../services/thue';

dayjs.extend(quarterOfYear);

const QuanTriTongHopThue = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [dataTable, setDataTable] = useState<Array<any>>([]);

    // Bộ lọc danh sách
    const [loaiChuKyFilter, setLoaiChuKyFilter] = useState<number>(0);
    const [thoiGianFilter, setThoiGianFilter] = useState<string | null>(dayjs().format('YYYY'));
    const [dateFilterValue, setDateFilterValue] = useState<Dayjs | null>(dayjs());

    // Modal khởi tạo
    const [form] = Form.useForm();
    const [openModalKhoiTao, setOpenModalKhoiTao] = useState<boolean>(false);
    const [loaiChuKyKT, setLoaiChuKyKT] = useState<number>(loaiChuKyThue.thang);

    // States danh sách kỳ tham chiếu cho Modal
    const [dsKyThueTNCNModal, setDsKyThueTNCNModal] = useState<Array<DanhSachThueThangResponse>>([]);
    const [dsKyThueKTXModal, setDsKyThueKTXModal] = useState<Array<KyThueResponse>>([]);
    const [namLocTNCN, setNamLocTNCN] = useState<Dayjs | null>(dayjs());
    const [namLocKTX, setNamLocKTX] = useState<Dayjs | null>(dayjs());

    const layDuLieu = async (thoiGian: string | null, loaiChuKy: number) => {
        try {
            setIsLoading(true);
            const rs = await timKiemDsThueTongHop(1, 1000, thoiGian || undefined, loaiChuKy);
            setDataTable((rs?.data || []).map((v: any) => ({ ...v, key: v.id })));
        } catch (err: any) {
            toast.error(err?.toString().replace("Error: ", ""));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        layDuLieu(thoiGianFilter, loaiChuKyFilter);
    }, []);

    const onChangeLoaiChuKyFilter = (val: number) => {
        setLoaiChuKyFilter(val);
        let newDateStr: string | null = null;
        if (val === 0 || val === loaiChuKyThue.nam) {
            newDateStr = dayjs().format('YYYY');
            setDateFilterValue(dayjs());
        } else if (val === loaiChuKyThue.thang) {
            newDateStr = dayjs().startOf('month').format('DD/MM/YYYY');
            setDateFilterValue(dayjs());
        } else if (val === loaiChuKyThue.quy) {
            newDateStr = `${dayjs().quarter()}/${dayjs().year()}`;
            setDateFilterValue(dayjs());
        }
        setThoiGianFilter(newDateStr);
        layDuLieu(newDateStr, val);
    };

    const onChangeFilterDate = (date: Dayjs | null) => {
        setDateFilterValue(date);
        if (!date) {
            setThoiGianFilter(null);
            layDuLieu(null, loaiChuKyFilter);
            return;
        }

        let dateStr = '';
        if (loaiChuKyFilter === 0 || loaiChuKyFilter === loaiChuKyThue.nam) {
            dateStr = date.format('YYYY');
        } else if (loaiChuKyFilter === loaiChuKyThue.thang) {
            dateStr = date.startOf('month').format('DD/MM/YYYY');
        } else if (loaiChuKyFilter === loaiChuKyThue.quy) {
            dateStr = `${date.quarter()}/${date.year()}`;
        }
        setThoiGianFilter(dateStr);
        layDuLieu(dateStr, loaiChuKyFilter);
    };

    // Tải danh sách kỳ tham chiếu cho modal
    const layDsThamChieuChoModal = async (namTNCN?: number, namKTX?: number) => {
        try {
            const [tncn, ktx] = await Promise.all([
                layDsThueThangTheoNam(namTNCN),
                layDanhSachKyThueKhongThuongXuyen(namKTX ? namKTX.toString() : "")
            ]);
            setDsKyThueTNCNModal(tncn || []);
            setDsKyThueKTXModal(ktx || []);
        } catch (err: any) {
            toast.error("Không thể lấy danh sách các kỳ thuế tham chiếu");
        }
    };

    const handleOpenModalKhoiTao = () => {
        const curYear = dayjs().year();
        setNamLocTNCN(dayjs().year(curYear));
        setNamLocKTX(dayjs().year(curYear));
        setLoaiChuKyKT(loaiChuKyThue.thang);

        form.resetFields();
        form.setFieldsValue({
            loaiChuKy: loaiChuKyThue.thang,
            thoiGian: dayjs(),
            quy: dayjs().quarter(),
            nam: dayjs(),
            dsIdThueTNCN: [],
            dsIdThueKTX: []
        });

        layDsThamChieuChoModal(curYear, curYear);
        setOpenModalKhoiTao(true);
    };

    const onFinishKhoiTao = async (values: any) => {
        try {
            let thangNam = '';
            if (values.loaiChuKy === loaiChuKyThue.thang) {
                thangNam = (values.thoiGian as Dayjs).format('MM/YYYY');
            } else if (values.loaiChuKy === loaiChuKyThue.quy) {
                const nam = values.nam ? (values.nam as Dayjs).year() : dayjs().year();
                thangNam = `${values.quy}/${nam}`;
            } else {
                thangNam = (values.thoiGian as Dayjs).format('YYYY');
            }

            const request = {
                loaiChuKy: values.loaiChuKy,
                thangNam: thangNam,
                dsIdThueTNCN: values.dsIdThueTNCN || [],
                dsIdThueKTX: values.dsIdThueKTX || []
            };

            setIsLoading(true);
            const res = await khoiTaoThueTongHop(request);
            toast.success("Khởi tạo kỳ tổng hợp thuế thành công!");
            setOpenModalKhoiTao(false);

            if (res && res.id) {
                navigate(`${Links.Thue.ChiTietTongHopThue}?id=${res.id}`);
            } else {
                layDuLieu(thoiGianFilter, loaiChuKyFilter);
            }
        } catch (err: any) {
            toast.error(err?.toString().replace("Error: ", ""));
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        try {
            setIsLoading(true);
            await xoaThueTongHop(id);
            toast.success("Xóa kỳ tổng hợp thuế thành công!");
            layDuLieu(thoiGianFilter, loaiChuKyFilter);
        } catch (err: any) {
            toast.error(err?.toString().replace("Error: ", ""));
        } finally {
            setIsLoading(false);
        }
    };

    const optionsTNCN = dsKyThueTNCNModal.map(x => ({
        value: x.id,
        label: `${x.tenKyThue} (Số NV: ${x.soNhanVien || 0})`
    }));

    const optionsKTX = dsKyThueKTXModal.map(x => ({
        value: x.id,
        label: `${x.kyThue} (Số NSK: ${x.soNhanVien || 0})`
    }));

    const columns: ColumnsType<any> = [
        {
            title: 'Kỳ tổng hợp thuế',
            dataIndex: 'tenKyThue',
            key: 'tenKyThue',
            render: (text) => <strong>{text}</strong>
        },
        {
            title: 'Loại chu kỳ',
            dataIndex: 'loaiChuKy',
            key: 'loaiChuKy',
            width: 130,
            align: 'center',
            render: (val) => {
                if (val === loaiChuKyThue.thang) return <Tag color="blue">Tháng</Tag>;
                if (val === loaiChuKyThue.quy) return <Tag color="geekblue">Quý</Tag>;
                if (val === loaiChuKyThue.nam) return <Tag color="purple">Năm</Tag>;
                return <Tag color="default">Khác</Tag>;
            }
        },
        {
            title: 'Số nhân sự',
            dataIndex: 'soNhanVien',
            key: 'soNhanVien',
            width: 120,
            align: 'center',
            render: (val) => val ?? '-'
        },
        {
            title: 'Tổng thu nhập chịu thuế',
            dataIndex: 'tongThuNhapChiuThue',
            key: 'tongThuNhapChiuThue',
            align: 'right',
            render: (text: number) => formatVND(text)
        },
        {
            title: 'Tổng thuế TNCN',
            dataIndex: 'tongThuePhaiNop',
            key: 'tongThuePhaiNop',
            align: 'right',
            render: (text: number) => <span style={{ color: '#d9534f', fontWeight: 600 }}>{formatVND(text)}</span>
        },
        {
            title: 'Trạng thái',
            dataIndex: 'trangThai',
            key: 'trangThai',
            width: 140,
            align: 'center',
            render: (value) => {
                if (value === trangThaiThue.khoiTao) return <Tag color="orange">Khởi tạo</Tag>;
                if (value === trangThaiThue.duyet) return <Tag color="green">Đã duyệt</Tag>;
                return <Tag color="default">Không xác định</Tag>;
            }
        },
        {
            title: 'Ngày tạo',
            dataIndex: 'ngayKhoiTao',
            key: 'ngayKhoiTao',
            width: 130,
            align: 'center',
            render: (val) => dateCommon.formatDate(val, 'dd/MM/yyyy')
        },
        {
            title: 'Thao tác',
            dataIndex: 'action',
            key: 'action',
            width: 140,
            align: 'center',
            render: (_: any, record: any) => (
                <div className="d-flex" style={{ gap: '8px' }}>
                    <Link className="hrm-btn hrm-btn-sm btn-primary" to={`${Links.Thue.ChiTietTongHopThue}?id=${record.id}`}>
                        Xem
                    </Link>
                    <Popconfirm
                        title="Bạn có chắc chắn muốn xóa kỳ tổng hợp này?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Đồng ý"
                        cancelText="Hủy"
                    >
                        <Button type="primary" danger size="small">Xóa</Button>
                    </Popconfirm>
                </div>
            )
        }
    ];

    return (
        <section>
            <ContentHeader title="Quản trị Tổng hợp Thuế" />

            <Row className="form-group" gutter={8}>
                <Col span={4}>
                    <Select
                        value={loaiChuKyFilter}
                        onChange={onChangeLoaiChuKyFilter}
                        options={[
                            { value: 0, label: 'Tất cả chu kỳ' },
                            { value: loaiChuKyThue.thang, label: 'Tháng' },
                            { value: loaiChuKyThue.quy, label: 'Quý' },
                            { value: loaiChuKyThue.nam, label: 'Năm' }
                        ]}
                        style={{ width: '100%' }}
                    />
                </Col>
                <Col span={6}>
                    <DatePicker
                        value={dateFilterValue}
                        onChange={onChangeFilterDate}
                        picker={
                            loaiChuKyFilter === 0 || loaiChuKyFilter === loaiChuKyThue.nam
                                ? "year"
                                : (loaiChuKyFilter === loaiChuKyThue.thang ? "month" : "quarter")
                        }
                        format={
                            loaiChuKyFilter === 0 || loaiChuKyFilter === loaiChuKyThue.nam
                                ? "YYYY"
                                : (loaiChuKyFilter === loaiChuKyThue.thang ? "MM/YYYY" : "Q/YYYY")
                        }
                        placeholder={
                            loaiChuKyFilter === 0 || loaiChuKyFilter === loaiChuKyThue.nam
                                ? 'Chọn năm'
                                : (loaiChuKyFilter === loaiChuKyThue.thang ? 'Chọn tháng' : 'Chọn quý')
                        }
                        style={{ width: '100%' }}
                    />
                </Col>
                <Col span={14}>
                    <Button
                        type="primary"
                        className="float-right"
                        onClick={handleOpenModalKhoiTao}
                        icon={<PlusOutlined />}
                    >
                        Khởi tạo kỳ tổng hợp
                    </Button>
                </Col>
            </Row>

            <Row>
                <Col span="24">
                    <div className="card card-primary">
                        <Table
                            columns={columns}
                            dataSource={dataTable}
                            loading={isLoading}
                            locale={{ emptyText: "Không có dữ liệu kỳ tổng hợp thuế" }}
                        />
                    </div>
                </Col>
            </Row>

            {openModalKhoiTao && (
                <ModalTextForm
                    title="Khởi tạo kỳ tổng hợp thuế"
                    hideFooter
                    show={openModalKhoiTao}
                    handleClose={() => setOpenModalKhoiTao(false)}
                >
                    <Form
                        layout="vertical"
                        validateMessages={validateMessages}
                        onFinish={onFinishKhoiTao}
                        form={form}
                    >
                        <Row gutter={12}>
                            <Col span={12}>
                                <Form.Item rules={[{ required: true }]} label="Loại chu kỳ" name="loaiChuKy">
                                    <Select
                                        onChange={(val) => setLoaiChuKyKT(val)}
                                        options={[
                                            { value: loaiChuKyThue.thang, label: 'Tháng' },
                                            { value: loaiChuKyThue.quy, label: 'Quý' },
                                            { value: loaiChuKyThue.nam, label: 'Năm' }
                                        ]}
                                    />
                                </Form.Item>
                            </Col>

                            {loaiChuKyKT === loaiChuKyThue.thang && (
                                <Col span={12}>
                                    <Form.Item rules={[{ required: true }]} label="Tháng tính thuế" name="thoiGian">
                                        <DatePicker
                                            picker="month"
                                            format="MM/YYYY"
                                            placeholder="Chọn tháng"
                                            style={{ width: "100%" }}
                                        />
                                    </Form.Item>
                                </Col>
                            )}

                            {loaiChuKyKT === loaiChuKyThue.quy && (
                                <>
                                    <Col span={6}>
                                        <Form.Item rules={[{ required: true }]} label="Quý" name="quy">
                                            <Select
                                                options={[
                                                    { value: 1, label: 'Quý 1' },
                                                    { value: 2, label: 'Quý 2' },
                                                    { value: 3, label: 'Quý 3' },
                                                    { value: 4, label: 'Quý 4' }
                                                ]}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={6}>
                                        <Form.Item rules={[{ required: true }]} label="Năm" name="nam">
                                            <DatePicker
                                                picker="year"
                                                format="YYYY"
                                                placeholder="Chọn năm"
                                                style={{ width: "100%" }}
                                            />
                                        </Form.Item>
                                    </Col>
                                </>
                            )}

                            {loaiChuKyKT === loaiChuKyThue.nam && (
                                <Col span={12}>
                                    <Form.Item rules={[{ required: true }]} label="Năm tính thuế" name="thoiGian">
                                        <DatePicker
                                            picker="year"
                                            format="YYYY"
                                            placeholder="Chọn năm"
                                            style={{ width: "100%" }}
                                        />
                                    </Form.Item>
                                </Col>
                            )}
                        </Row>

                        <div className="card p-2 bg-light mb-3 mt-2">
                            <strong style={{ color: '#0c4da2' }}>Các khoản / kỳ thuế tham chiếu:</strong>
                            <p className="text-muted mb-2" style={{ fontSize: 13 }}>
                                Chọn các kỳ Thuế TNCN (thường xuyên) và Thuế KTX (không thường xuyên) để tổng hợp lại vào kỳ này.
                            </p>

                            {/* Tham chiếu Thuế TNCN */}
                            <Row gutter={8} className="mb-2">
                                <Col span={8}>
                                    <Form.Item label="Năm lọc Thuế TNCN">
                                        <DatePicker
                                            picker="year"
                                            placeholder="Năm (để trống = tất cả)"
                                            style={{ width: "100%" }}
                                            value={namLocTNCN}
                                            onChange={async (d) => {
                                                setNamLocTNCN(d);
                                                const res = await layDsThueThangTheoNam(d ? d.year() : undefined);
                                                setDsKyThueTNCNModal(res || []);
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={16}>
                                    <Form.Item label="Chọn các kỳ Thuế TNCN (Thường xuyên)" name="dsIdThueTNCN">
                                        <Select
                                            mode="multiple"
                                            allowClear
                                            placeholder="Chọn các kỳ thuế TNCN..."
                                            options={optionsTNCN}
                                            notFoundContent="Không có dữ liệu"
                                            filterOption={(input, option: any) =>
                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                            }
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            {/* Tham chiếu Thuế KTX */}
                            <Row gutter={8}>
                                <Col span={8}>
                                    <Form.Item label="Năm lọc Thuế KTX">
                                        <DatePicker
                                            picker="year"
                                            placeholder="Năm (để trống = tất cả)"
                                            style={{ width: "100%" }}
                                            value={namLocKTX}
                                            onChange={async (d) => {
                                                setNamLocKTX(d);
                                                const res = await layDanhSachKyThueKhongThuongXuyen(d ? d.year().toString() : "");
                                                setDsKyThueKTXModal(res || []);
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={16}>
                                    <Form.Item label="Chọn các kỳ Thuế KTX (Không thường xuyên)" name="dsIdThueKTX">
                                        <Select
                                            mode="multiple"
                                            allowClear
                                            placeholder="Chọn các kỳ thuế KTX..."
                                            options={optionsKTX}
                                            notFoundContent="Không có dữ liệu"
                                            filterOption={(input, option: any) =>
                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                            }
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>

                        <Row justify="end">
                            <Space>
                                <Button onClick={() => setOpenModalKhoiTao(false)}>Hủy</Button>
                                <Button type="primary" htmlType="submit" loading={isLoading}>
                                    Khởi tạo & Xem chi tiết
                                </Button>
                            </Space>
                        </Row>
                    </Form>
                </ModalTextForm>
            )}
        </section>
    );
};

export default QuanTriTongHopThue;
